import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import InfoCard from '../InfoCard/InfoCard';
import DataModal from '../DataModal/DataModal';
import { SectionType } from '../DataModal/DataModal.interface';
import ContentSectionSkeleton from '../SectionSkeletons/ContentSectionSkeleton';
import LossOfExclusivityDetailModal from '../LossOfExclusivitySection/components/LossOfExclusivityDetailModal/LossOfExclusivityDetailModal';
import SolicitationDetailModal, {
  SolicitationDetailsResponse,
} from '../SolicitationDetailModal/SolicitationDetailModal';
import { ANDADetailModal } from 'components/shared/ANDADetailModal';
import { InfoCardData } from '../../useLandingPage.logic';
import { ANDAInfoCardData } from 'Interfaces/ProductWikiInterfaces';
import { LossOfExclusivityInfoCardData } from 'Interfaces/ProductWikiInterfaces/landingPage.types';
import { getColumnsBySectionType } from '../DataModal/modalColumnConfigs';
import { useStore } from 'hooks/useStore';
import { getSolicitationDetails } from 'services/productWikiServices';
import './PreSolicitationAlertsSection.styles.scss';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { PreSolicitationAlertsSectionProps } from './PreSolicitation.interface';

interface PeriodOption {
  label: string;
  value: number;
}

const PreSolicitationAlertsSection: React.FC<PreSolicitationAlertsSectionProps> = observer(
  ({ alerts }) => {
    const [showModal, setShowModal] = useState<boolean>(false);
    const [currentSection, setCurrentSection] = useState<SectionType>('preSolicitationAlerts');
    // LOE detail modal state
    const [showLoeDetailModal, setShowLoeDetailModal] = useState<boolean>(false);
    const [selectedLoeCard, setSelectedLoeCard] = useState<LossOfExclusivityInfoCardData | null>(
      null
    );
    // ANDA detail modal state
    const [showAndaDetailModal, setShowAndaDetailModal] = useState<boolean>(false);
    const [selectedAnda, setSelectedAnda] = useState<ANDAInfoCardData | null>(null);
    // Solicitation detail modal state
    const [showSolicitationDetailModal, setShowSolicitationDetailModal] = useState<boolean>(false);
    const [solicitationDetails, setSolicitationDetails] =
      useState<SolicitationDetailsResponse | null>(null);
    const [isSolicitationDetailsLoading, setIsSolicitationDetailsLoading] =
      useState<boolean>(false);

    const {
      LandingPageStore: {
        fetchContractExpirations,
        fetchAndaUpdates,
        fetchLossOfExclusivity,
        isContractExpirationsLoading,
        isAndaUpdatesLoading,
        isLossOfExclusivityLoading,
        isPreSolicitationAlertsLoading,
        landingPageData,
        selectedContractExpirationsPeriod,
        selectedAndaUpdatesPeriod,
        selectedLossOfExclusivityPeriod,
      },
    } = useStore();

    const handleContractPeriodChange = async (e: { value: number | string }) => {
      const periodValue = typeof e.value === 'number' ? e.value : parseInt(e.value as string, 10);
      await fetchContractExpirations(periodValue);
    };

    const handleAndaPeriodChange = async (e: { value: number | string }) => {
      const periodValue = typeof e.value === 'number' ? e.value : parseInt(e.value as string, 10);
      await fetchAndaUpdates(periodValue);
    };

    const handleLoePeriodChange = async (e: { value: number | string }) => {
      const periodValue = typeof e.value === 'number' ? e.value : parseInt(e.value as string, 10);
      await fetchLossOfExclusivity(periodValue);
    };

    // Get loading state for current section
    const getCurrentSectionLoading = (): boolean => {
      switch (currentSection) {
        case 'contractExpirations':
          return isContractExpirationsLoading;
        case 'andaUpdates':
          return isAndaUpdatesLoading;
        case 'lossOfExclusivity':
          return isLossOfExclusivityLoading;
        case 'preSolicitationAlerts':
          return isPreSolicitationAlertsLoading;
        default:
          return false;
      }
    };

    const periodOptions = [
      { label: '3 months', value: 90 },
      { label: '6 months', value: 180 },
      { label: '12 months', value: 365 },
    ];

    const andaPeriodOptions: PeriodOption[] = [
      { label: 'Past 3 months', value: 90 },
      { label: 'Past 6 months', value: 180 },
      { label: 'Past 12 months', value: 365 },
    ];

    const displayedAlerts = alerts.slice(0, 6);
    const hasMoreAlerts = alerts.length > 6;

    // Get data for current section
    const getSectionData = (section: SectionType): InfoCardData[] => {
      if (!landingPageData) return [];

      switch (section) {
        case 'contractExpirations':
          return landingPageData.contractExpirations || [];
        case 'andaUpdates':
          return landingPageData.andaUpdates || [];
        case 'preSolicitationAlerts':
          return landingPageData.preSolicitationAlerts || [];
        case 'lossOfExclusivity':
          return landingPageData.lossOfExclusivity || [];
        // case 'marketIntelligence':
        //   return landingPageData.marketIntelligence || [];
        default:
          return [];
      }
    };

    // Handle section change in modal
    const handleSectionChange = (section: SectionType) => {
      setCurrentSection(section);
    };

    // Handle LOE card click to open detail modal (for when user switches to LOE section)
    const handleLoeCardClick = (item: LossOfExclusivityInfoCardData) => {
      setSelectedLoeCard(item);
      setShowLoeDetailModal(true);
    };

    // Handle ANDA card click to open detail modal (for when user switches to ANDA section)
    const handleAndaCardClick = (item: ANDAInfoCardData) => {
      setSelectedAnda(item);
      setShowAndaDetailModal(true);
    };

    // Handle Solicitation card click to fetch details and open modal
    const handleSolicitationCardClick = async (item: InfoCardData) => {
      console.log('item', item);
      const solicitationNumber = item.solicitation_number;
      if (!solicitationNumber) {
        console.error('No solicitation number available for this item');
        return;
      }

      setIsSolicitationDetailsLoading(true);
      setShowSolicitationDetailModal(true);

      try {
        const response = await getSolicitationDetails(solicitationNumber);
        setSolicitationDetails(response.data);
      } catch (error) {
        console.error('Error fetching solicitation details:', error);
        setSolicitationDetails(null);
      } finally {
        setIsSolicitationDetailsLoading(false);
      }
    };

    // Handle row click based on current section
    const handleRowClick = (row: InfoCardData | ANDAInfoCardData | LossOfExclusivityInfoCardData) => {
      if (currentSection === 'andaUpdates') {
        handleAndaCardClick(row as ANDAInfoCardData);
      } else if (currentSection === 'lossOfExclusivity') {
        handleLoeCardClick(row as LossOfExclusivityInfoCardData);
      } else if (currentSection === 'preSolicitationAlerts') {
        handleSolicitationCardClick(row as InfoCardData);
      }
    };

    // Get columns for current section
    const columns = getColumnsBySectionType(currentSection);
    const modalData = getSectionData(currentSection);

    return (
      <>
        <section className="content-section">
          <div className="section-header">
            <div className="section-title">
              <i
                className="fas fa-exclamation-triangle"
                style={{ color: 'var(--error-color)' }}
              ></i>
              Solicitation Alerts
              <span className="section-badge">{alerts.length} New</span>
            </div>
            {/* <div className="period-selector">
              <Dropdown
                value={selectedPreSolicitationAlertsFilter}
                options={filterOptions}
                onChange={handleFilterChange}
                placeholder="Select Filter"
                className="period-dropdown"
                appendTo={typeof document !== 'undefined' ? document.body : undefined}
              />
            </div> */}
          </div>
          <div className="section-content">
            {isPreSolicitationAlertsLoading ? (
              <ContentSectionSkeleton />
            ) : alerts.length === 0 ? (
              <div className="no-data-message">No data available for Pre-Solicitation Alerts</div>
            ) : (
              <>
                <div className="three-card-grid">
                  {displayedAlerts.map((alert, index) => (
                    <div key={index} className="three-card-wrapper">
                      <InfoCard {...alert} onCardClick={() => handleSolicitationCardClick(alert)} />
                    </div>
                  ))}
                </div>
                {hasMoreAlerts && (
                  <div className="see-more-container">
                    <button className="see-more-btn" onClick={() => setShowModal(true)}>
                      See More
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        <DataModal
          visible={showModal}
          onHide={() => {
            setShowModal(false);
            setCurrentSection('preSolicitationAlerts'); // Reset to default section when closing
          }}
          data={modalData}
          sectionType={currentSection}
          columns={columns}
          onSectionChange={handleSectionChange}
          sectionDropdownConfigs={{
            contractExpirations: {
              value: selectedContractExpirationsPeriod,
              options: periodOptions,
              onChange: handleContractPeriodChange,
              placeholder: 'Select Period',
            },
            andaUpdates: {
              value: selectedAndaUpdatesPeriod,
              options: andaPeriodOptions,
              onChange: handleAndaPeriodChange,
              placeholder: 'Select Period',
            },
            lossOfExclusivity: {
              value: selectedLossOfExclusivityPeriod,
              options: periodOptions,
              onChange: handleLoePeriodChange,
              placeholder: 'Select Period',
            },
            // preSolicitationAlerts: {
            //   value: selectedPreSolicitationAlertsFilter,
            //   options: filterOptions,
            //   onChange: handleFilterChange,
            //   placeholder: 'Select Filter',
            // },
          }}
          isLoading={getCurrentSectionLoading()}
          onRowClick={
            currentSection === 'andaUpdates' ||
            currentSection === 'lossOfExclusivity' ||
            currentSection === 'preSolicitationAlerts'
              ? handleRowClick
              : undefined
          }
        />

        {/* LOE Detail Modal - for when user switches to LOE section */}
        <LossOfExclusivityDetailModal
          visible={showLoeDetailModal}
          onHide={() => {
            setShowLoeDetailModal(false);
            setSelectedLoeCard(null);
          }}
          data={selectedLoeCard}
        />

        {/* ANDA Detail Modal - for when user switches to ANDA section */}
        <ANDADetailModal
          visible={showAndaDetailModal}
          onHide={() => {
            setShowAndaDetailModal(false);
            setSelectedAnda(null);
          }}
          data={selectedAnda}
        />

        {/* Solicitation Detail Modal - for solicitation alerts */}
        <SolicitationDetailModal
          visible={showSolicitationDetailModal}
          onHide={() => {
            setShowSolicitationDetailModal(false);
            setSolicitationDetails(null);
          }}
          data={solicitationDetails}
          isLoading={isSolicitationDetailsLoading}
        />
      </>
    );
  }
);

export default PreSolicitationAlertsSection;
